import { type ReactNode, useState, lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import AuthInitializer from './AuthInitializer';

// 개발 환경에서만 lazy load
const ReactQueryDevtools = lazy(() =>
    import('@tanstack/react-query-devtools').then((module) => ({
        default: module.ReactQueryDevtools,
    }))
);

type QueryProviderProps = {
    children: ReactNode;
}

const queryClientConfig = {
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60, // 기본 staleTime: 1분
            gcTime: 1000 * 60 * 5, // 기본 gcTime(cacheTime): 5분
            refetchOnWindowFocus: false, // 창 포커스 시 자동 refetch 비활성화
            refetchOnMount: false, // 마운트 시 자동 refetch 비활성화
            refetchOnReconnect: true, // 재연결 시 자동 refetch
            retry: 1, // 실패 시 재시도 횟수
            retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000) // 재시도 딜레이
        },
        mutations: {
            retry: false // mutation은 실패 후 재시도 안함
        }
    }
};

export default function QueryProvider({ children }: QueryProviderProps) {
    const [queryClient] = useState(() => new QueryClient(queryClientConfig));

    return (
        <QueryClientProvider client={queryClient}>
            <AuthInitializer>{children}</AuthInitializer>
            {process.env.NODE_ENV === 'development' && (
                <Suspense fallback={null}>
                    <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
                </Suspense>
            )}
        </QueryClientProvider>
    );
}
